import { useEffect, useMemo, useState } from 'react'
import {
  Download,
  Link2,
  Loader2,
  Lock,
  Menu,
  Pencil,
  Target,
  Trash2,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/Modal'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { downloadAuthenticatedFile } from '@/lib/downloadBlob'
import { useAuth } from '@/auth/useAuth'
import { useToast } from '@/hooks/useToast'
import {
  useChatThreads,
  useDeleteChatThread,
  useRenameChatThread,
  useSetChatThreadVisibility,
  useThreadVisibility,
} from './queries'
import { ShareLinkDialog } from './ShareLinkDialog'
import { VisibilityPicker, type VisibilityValue } from './VisibilityPicker'
import type { ChatThreadSummary } from './types'

interface Props {
  threadId: string | null
  onAfterDelete: () => void
  sidebarCollapsed: boolean
  onToggleSidebar: () => void
}

export function ChatHeader({
  threadId,
  onAfterDelete,
  sidebarCollapsed,
  onToggleSidebar,
}: Props) {
  const threadsQ = useChatThreads()
  const renameM = useRenameChatThread()
  const deleteM = useDeleteChatThread()
  const visM = useSetChatThreadVisibility()
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const canShareAll = hasPermission('chatbot.share.all')
  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [sharingOpen, setSharingOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [visibilityOpen, setVisibilityOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const thread: ChatThreadSummary | null =
    threadsQ.data?.threads.find((t) => t.id === threadId) ?? null

  // Hydrate the picker with the existing share lists so re-opening it
  // shows previously-picked roles instead of an empty form. Only fires
  // when the popover is open and a thread is loaded.
  const visibilityQ = useThreadVisibility(visibilityOpen ? threadId : null)
  const pickerInitial = useMemo<Partial<VisibilityValue> | undefined>(() => {
    if (!thread) return undefined
    return {
      visibility: visibilityQ.data?.visibility ?? thread.visibility,
      sharedRoleIds: (visibilityQ.data?.shared_roles ?? []).map((r) => r.role_id),
      sharedUserIds: (visibilityQ.data?.shared_users ?? []).map((u) => u.user_id),
    }
  }, [thread, visibilityQ.data])

  useEffect(() => {
    // Reset rename UI when the active thread changes.
    setRenaming(false)
  }, [threadId])

  const startRename = () => {
    if (!thread) return
    setRenameValue(thread.title)
    setRenaming(true)
  }

  const commitRename = async () => {
    if (!thread) return
    const title = renameValue.trim()
    if (!title || title === thread.title) {
      setRenaming(false)
      return
    }
    await renameM.mutateAsync({ id: thread.id, title })
    setRenaming(false)
  }

  const performDelete = async () => {
    if (!thread) return
    try {
      await deleteM.mutateAsync(thread.id)
      onAfterDelete()
    } finally {
      setDeleteOpen(false)
    }
  }

  const applyVisibility = async (v: VisibilityValue) => {
    if (!thread) return
    try {
      await visM.mutateAsync({
        id: thread.id,
        visibility: v.visibility,
        sharedRoleIds: v.sharedRoleIds,
        sharedUserIds: v.sharedUserIds,
      })
      toast({
        title:
          v.visibility === 'tenant'
            ? 'Chat shared with the workspace. Members notified.'
            : v.visibility === 'shared'
              ? 'Chat shared with the selected roles. Members notified.'
              : 'Chat is private to you again.',
      })
      setVisibilityOpen(false)
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Could not change chat visibility',
        description: e instanceof Error ? e.message : 'Please try again.',
      })
    }
  }

  const exportPdf = async () => {
    if (!thread || exporting) return
    setExporting(true)
    try {
      const safe = (thread.title || 'chat')
        .replace(/[^A-Za-z0-9._-]+/g, '_')
        .slice(0, 60)
      await downloadAuthenticatedFile(
        `/api/chatbot/threads/${thread.id}/export.pdf`,
        `${safe}.pdf`,
      )
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Failed to export chat as PDF.')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-3 py-2 backdrop-blur">
      {sidebarCollapsed ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={onToggleSidebar}
          aria-label="Show chat history"
          title="Show chat history"
        >
          <Menu className="size-4" />
        </Button>
      ) : null}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {renaming && thread ? (
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.currentTarget.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename()
              else if (e.key === 'Escape') setRenaming(false)
            }}
            autoFocus
            className="h-8 text-sm"
          />
        ) : (
          <h2 className="min-w-0 truncate text-sm font-semibold text-foreground">
            {thread ? thread.title : 'New chat'}
          </h2>
        )}
        {thread ? (
          canShareAll ? (
            // The badge IS the picker trigger when the user can share.
            // One click opens a popover with Private / Workspace /
            // Specific roles options + a searchable role list.
            <Popover open={visibilityOpen} onOpenChange={setVisibilityOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="shrink-0"
                  aria-label="Change chat visibility"
                  title="Click to change who can see this chat"
                >
                  <Badge
                    variant={
                      thread.visibility === 'tenant'
                        ? 'success'
                        : thread.visibility === 'shared'
                          ? 'info'
                          : 'secondary'
                    }
                    className="cursor-pointer gap-1 text-[10px] hover:opacity-90"
                  >
                    {thread.visibility === 'tenant' ? (
                      <Users className="size-3" aria-hidden />
                    ) : thread.visibility === 'shared' ? (
                      <Target className="size-3" aria-hidden />
                    ) : (
                      <Lock className="size-3" aria-hidden />
                    )}
                    {thread.visibility === 'tenant'
                      ? 'Workspace'
                      : thread.visibility === 'shared'
                        ? 'Shared'
                        : 'Private'}
                  </Badge>
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Who can see this chat
                </div>
                <VisibilityPicker
                  initial={pickerInitial}
                  onApply={applyVisibility}
                  onCancel={() => setVisibilityOpen(false)}
                  busy={visM.isPending}
                  applyLabel="Save"
                />
              </PopoverContent>
            </Popover>
          ) : (
            // No share permission: badge is read-only, no popover.
            <Badge
              variant={thread.visibility === 'tenant' ? 'success' : 'secondary'}
              className="shrink-0 text-[10px]"
              title={
                thread.visibility === 'tenant'
                  ? 'Visible to every member of this workspace.'
                  : thread.visibility === 'shared'
                    ? 'Shared with selected roles or members.'
                    : 'Only you can see this chat.'
              }
            >
              {thread.visibility === 'tenant'
                ? 'Workspace'
                : thread.visibility === 'shared'
                  ? 'Shared'
                  : 'Private'}
            </Badge>
          )
        ) : null}
      </div>
      {thread ? (
        <div className="flex shrink-0 items-center gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 text-muted-foreground hover:text-foreground"
            onClick={startRename}
            aria-label="Rename chat"
            title="Rename"
            disabled={renaming}
          >
            <Pencil className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 text-muted-foreground hover:text-foreground"
            onClick={() => setSharingOpen(true)}
            aria-label="Share chat as public link"
            title="Share as public link"
          >
            <Link2 className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 text-muted-foreground hover:text-foreground"
            onClick={exportPdf}
            disabled={exporting}
            aria-label="Export chat as PDF"
            title="Export as PDF"
          >
            {exporting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Download className="size-4" />
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 text-muted-foreground hover:text-destructive"
            onClick={() => setDeleteOpen(true)}
            aria-label="Delete chat"
            title="Delete"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ) : null}
      <ShareLinkDialog
        open={sharingOpen}
        threadId={thread?.id ?? null}
        onClose={() => setSharingOpen(false)}
      />
      <Modal.Alert
        open={!!exportError}
        onOpenChange={(o) => (o ? null : setExportError(null))}
        title="Export failed"
        body={exportError || 'Failed to export chat as PDF.'}
        onClose={() => setExportError(null)}
      />
      <Modal.Confirm
        open={deleteOpen}
        onOpenChange={(o) => (o ? null : setDeleteOpen(false))}
        title="Delete this chat?"
        body={
          thread
            ? `"${thread.title}" will be permanently removed. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={performDelete}
        busy={deleteM.isPending}
      />
    </div>
  )
}
